import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal, Signal, untracked } from '@angular/core';
import { DataService } from '@app/shared/services/data-service';
import { TagDTO } from '@app/shared/services/dtos/project-tag/tag.dto';
import { LoaderService } from '@app/shared/services/shared/loader.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { CreateProjectTagRequestBody, Tag, UpdateProjectTagRequestBody } from '@app/shared/types/models/tag/tag.model';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { finalize, Observable, tap } from 'rxjs';
import { ActiveProjectService } from './active-project.service';
import { ProjectTagFactory } from './factories/project-tag.factory';

/**
 * TagsDataService - Project tags and task-tag associations in the store.
 *
 * Loads project tags when {@link ActiveProjectService.activeProjectId} changes (constructor `effect`).
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Tag management views, task filters, and task tagging flows.
 *
 * Architecture:
 * - {@link TagsDataService}: Store and HTTP
 * - {@link ActiveProjectService}: Triggers load for the active project’s tags
 */
@Injectable({ providedIn: 'root' })
export class TagsDataService extends DataService<TagDTO, Tag> {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly loaderService: LoaderService = inject(LoaderService);
  private readonly projectTagFactory: ProjectTagFactory = inject(ProjectTagFactory);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService = inject(TranslateService);

  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);
  private tags: DataStore<Tag> = new DataStore<Tag>();
  private projectTags: DataStore<IdsCollection> = new DataStore<IdsCollection>();
  private taskTags: DataStore<IdsCollection> = new DataStore<IdsCollection>();

  public constructor() {
    super();
    effect(() => {
      const projectId = this.activeProjectService.activeProjectId();
      if (!projectId) {
        return;
      }
      untracked(() => {
        this.loadProjectTags(projectId);
      });
    });
  }

  public getProjectTags(projectId: string): Signal<Tag[] | undefined> {
    if (!projectId) {
      return signal([]);
    }

    if (!this.projectTags.hasDataForId(projectId)) {
      this.fetchProjectTags(projectId);
    }

    return computed(
      () =>
        this.projectTags
          .get(projectId)()
          ?.ids?.map(id => this.tags.get(id)()) || []
    );
  }

  public getById(tagId: string): Signal<Tag | undefined> {
    return computed(() => this.tags.get(tagId)());
  }

  public getTaskTags(taskId: string): Signal<Tag[] | undefined> {
    if (!taskId) {
      return signal(undefined);
    }

    if (!this.taskTags.hasDataForId(taskId)) {
      this.fetchTaskTags(taskId);
    }

    return computed(
      () =>
        this.taskTags
          .get(taskId)()
          ?.ids?.map(id => this.tags.get(id)()) || []
    );
  }

  public loadProjectTags(projectId: string): void {
    if (this.projectTags.hasDataForId(projectId)) {
      return;
    }
    this.fetchProjectTags(projectId);
  }

  public updateTag(projectId: string, tagId: string, request: UpdateProjectTagRequestBody): Observable<TagDTO> {
    this.loaderService.startLoading();
    return this.httpClient.put<TagDTO>(environment.APIEndPoint + `projects/${projectId}/tags/${tagId}`, request).pipe(
      tap((tag: TagDTO) => {
        this.upsertLocalData(tag);
        this.toastService.success(
          this.translateService.instant('project-tags.toasts.successfully-edited', {
            tagName: tag.name
          })
        );
      }),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  public createTag(projectId: string, request: CreateProjectTagRequestBody): Observable<TagDTO> {
    this.loaderService.startLoading();
    return this.httpClient.post<TagDTO>(environment.APIEndPoint + `projects/${projectId}/tags`, request).pipe(
      tap((tag: TagDTO) => {
        this.upsertLocalData(tag);
        const existingProjectTags: string[] = this.projectTags.get(projectId)().ids ?? [];
        this.projectTags.upsert({ id: projectId, ids: existingProjectTags.concat(tag.id) });
        this.toastService.success(
          this.translateService.instant('project-tags.toasts.successfully-created', {
            tagName: tag.name
          })
        );
      }),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  public deleteTag(projectId: string, tag: Tag): Observable<void> {
    this.loaderService.startLoading();
    return this.httpClient.delete<void>(environment.APIEndPoint + `projects/${projectId}/tags/${tag.id}`).pipe(
      tap(() => {
        const existingProjectTags: string[] = this.projectTags.get(projectId)().ids ?? [];
        this.projectTags.upsert({
          id: projectId,
          ids: existingProjectTags.filter(existingTag => existingTag !== tag.id)
        });
        this.tags.delete(tag.id);
        this.toastService.success(
          this.translateService.instant('project-tags.toasts.successfully-deleted', {
            tagName: tag.name
          })
        );
      }),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  public fetchProjectTags(projectId: string): void {
    this.httpClient
      .get<TagDTO[]>(environment.APIEndPoint + `projects/${projectId}/tags`)
      .subscribe((tags: TagDTO[]) => {
        this.tags.upsertMany(tags.map(itm => this.projectTagFactory.produce(itm)));

        this.projectTags.upsert({
          id: projectId,
          ids: tags.map(itm => itm.id)
        });
      });
  }

  public fetchTaskTags(taskId: string): void {
    this.httpClient.get<TagDTO[]>(environment.APIEndPoint + `tasks/${taskId}/tags`).subscribe({
      next: (tags: TagDTO[]) => {
        this.tags.upsertMany(tags.map(itm => this.projectTagFactory.produce(itm)));
        this.taskTags.upsert({
          id: taskId,
          ids: tags.map(itm => itm.id)
        });
      },
      error: () => {}
    });
  }

  public updateTaskTags(taskId: string, tagIds: string[]): Observable<TagDTO[]> {
    return this.httpClient.post<TagDTO[]>(environment.APIEndPoint + `tasks/${taskId}/tags`, { tagIds: tagIds }).pipe(
      tap((tags: TagDTO[]) => {
        this.tags.upsertMany(tags.map(itm => this.projectTagFactory.produce(itm)));
        this.taskTags.upsert({
          id: taskId,
          ids: tags.map(itm => itm.id)
        });

        this.toastService.success(this.translateService.instant('project-tags.toasts.successfully-added-to-task'));
      })
    );
  }

  public upsertLocalData(dto: TagDTO): Signal<Tag> {
    return dto ? this.tags.upsert(this.projectTagFactory.produce(dto)) : signal(undefined);
  }
}
